import { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { format, subDays, startOfMonth } from 'date-fns';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

interface DateRangeFilterProps {
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
}

const PRESETS = [
  { label: 'Today', getRange: () => ({ from: new Date(), to: new Date() }) },
  { label: 'Last 7 days', getRange: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
  { label: 'Last 30 days', getRange: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { label: 'This month', getRange: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
];

export function DateRangeFilter({ from, to, onChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState<Date | undefined>(from ? new Date(from) : undefined);
  const [toDate, setToDate] = useState<Date | undefined>(to ? new Date(to) : undefined);

  const hasSelection = from !== null || to !== null;

  function formatDisplay(): string {
    if (!from && !to) return 'Date Range';
    if (from && to) {
      const f = format(new Date(from), 'MMM d');
      const t = format(new Date(to), 'MMM d');
      return f === t ? f : `${f} - ${t}`;
    }
    if (from) return `From ${format(new Date(from), 'MMM d')}`;
    return `Until ${format(new Date(to!), 'MMM d')}`;
  }

  function applyRange() {
    onChange(
      fromDate ? fromDate.toISOString() : null,
      toDate ? toDate.toISOString() : null,
    );
    setOpen(false);
  }

  function clearRange() {
    setFromDate(undefined);
    setToDate(undefined);
    onChange(null, null);
    setOpen(false);
  }

  function applyPreset(preset: (typeof PRESETS)[number]) {
    const range = preset.getRange();
    setFromDate(range.from);
    setToDate(range.to);
    onChange(range.from.toISOString(), range.to.toISOString());
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
            hasSelection
              ? 'border-brand-rose/40 bg-brand-rose/5 text-brand-rose'
              : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
          }`}
        >
          {formatDisplay()}
          <ChevronDown className="w-3 h-3 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Presets sidebar */}
          <div className="border-r border-slate-200 p-2 space-y-1 min-w-[120px]">
            <p className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">Quick</p>
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => applyPreset(preset)}
                className="block w-full text-left px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50 rounded-md transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Calendar pickers */}
          <div className="p-3">
            <div className="flex gap-4">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1 px-3">From</p>
                <Calendar
                  mode="single"
                  selected={fromDate}
                  onSelect={setFromDate}
                  disabled={(date) => (toDate ? date > toDate : false)}
                  initialFocus
                />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1 px-3">To</p>
                <Calendar
                  mode="single"
                  selected={toDate}
                  onSelect={setToDate}
                  disabled={(date) => (fromDate ? date < fromDate : false)}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-slate-200">
              {hasSelection && (
                <button
                  onClick={clearRange}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-brand-rose rounded-md hover:bg-slate-50 transition-colors"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              )}
              <button
                onClick={applyRange}
                className="px-4 py-1.5 text-xs font-semibold text-white rounded-md bg-gradient-to-r from-brand-rose to-brand-orange hover:shadow-md transition-all"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
